import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { StyledTextButton, StyledPopup } from './styles';
import { ESC } from '../../../constants/keycodes';
import { Copilot as CopilotIcon } from '@icons/crm';
import Tabs from '../../Tabs';
import Tab from '../../Tab';
import Button from '../../Button';

const LinkToolBar = React.memo(({ onApply, suggestions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  useEffect(() => {
    if (!!suggestions.length) setSuggestion(suggestions[0]);
  }, [suggestions]);

  const toggleIsOpen = useCallback(() => {
    setIsOpen(prevIsOpen => !prevIsOpen);
  });

  const handleClick = event => {
    event.stopPropagation();
    event.preventDefault();

    toggleIsOpen();
  };

  const handleKeyDown = event => {
    if (isOpen && event.keyCode === ESC) {
      toggleIsOpen();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, []);

  const handleSubmit = useCallback(() => {
    onApply(suggestion);
    toggleIsOpen();
  }, [suggestion, onApply]);

  const handleTab = useCallback(
    value => {
      const selected = suggestions.find(({ text }) => text === value);
      setSuggestion(selected);
    },
    [suggestions, suggestion]
  );

  return (
    (!!suggestions.length && (
      <>
        <StyledTextButton type="button" onClick={handleClick}>
          <CopilotIcon width={15} height={15} />
        </StyledTextButton>
        <StyledPopup isOpen={isOpen}>
          {isOpen && (
            <StyledWrapper>
              <StyledTabs
                functional
                value={suggestion?.text}
                onChange={handleTab}
              >
                {suggestions.map(({ id, text }, i) => (
                  <StyledTab
                    key={id}
                    value={text}
                    functional
                    autoFocus={text === suggestion?.text}
                  >
                    {`Подсказка ${i + 1}`}
                  </StyledTab>
                ))}
              </StyledTabs>
              <StyledPreview>{suggestion?.text}</StyledPreview>
              <StyledControl>
                <StyledCancelButton onClick={toggleIsOpen}>
                  Отмена
                </StyledCancelButton>
                <StyledButton onClick={handleSubmit}>Использовать</StyledButton>
              </StyledControl>
            </StyledWrapper>
          )}
        </StyledPopup>
      </>
    )) ||
    null
  );
});

LinkToolBar.propTypes = {
  suggestions: PropTypes.arrayOf(PropTypes.string),
  onApply: PropTypes.func,
};

LinkToolBar.defaultProps = {
  suggestions: [],
  onApply: Function.prototype,
};

const StyledWrapper = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 10px;
  width: 620px;
`;

const StyledTabs = styled(Tabs)`
  display: flex;
`;

const StyledTab = styled(Tab)`
  padding: 10px 0;
  cursor: pointer;
  font-weight: bold;
  border-bottom: 2px solid #dbecff;
  flex: 1;
  border-radius: 4px 4px 0 0;

  ${props =>
    props.autoFocus &&
    `
    border-bottom: 2px solid #007aff;
    color: #007aff;
    background-image: linear-gradient(#dbecff 0%, #add1f8 100%);
  `}
`;

const StyledPreview = styled(ReactMarkdown)`
  background-image: linear-gradient(180deg, #add1f8 0%, #dbecff 100%);
  border-radius: 0 0 4px 4px;
  max-height: 320px;
  height: 320px;
  overflow: hidden auto;
  padding: 10px;
  margin-bottom: 10px;
  white-space: pre-wrap;

  ol {
    list-style-position: revert;
    margin-left: 0.75em;
    text-align: left;
  }
`;

const StyledControl = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledButton = styled(Button)`
  border-radius: 4px;
  color: white;
  background-color: #007aff;
  font-size: 14px;
  height: 42px;
  line-height: 24px;
  min-height: 25px;
  min-width: 25px;
  padding: 0;
  position: relative;
  text-align: center;

  ${props =>
    props.disabled &&
    `
    background: #c2c5cc;
    color: white;
  `}
`;

const StyledCancelButton = styled(Button)`
  border: none;
  border-radius: 4px;
  color: #4d4d4d;
  background-color: transparent;
  font-weight: normal;
  font-size: 14px;
  height: 42px;
  line-height: 24px;
  min-height: 25px;
  min-width: 25px;
  padding: 0;
  position: relative;
  text-align: center;
`;

export default LinkToolBar;
